import { useCallback, useEffect, useRef, useState } from "react";
import {
  FeatureCreditBuyResponse,
  useBuyFeatureCreditsMutation,
  useGetPaymentMethodQuery,
} from "../../../../services/api/featureCreditPackagesApi";
import { useGetUserEntitlementsQuery } from "../../../../services/api/user.api";
import { classifyBuyOutcome, getBuyErrorData, mapBuyError } from "../../../../utils/featureCreditPackages";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1500;

// The backend model-binds idempotencyKey as a Guid, so even the fallback path must stay a
// well-formed UUID. crypto.randomUUID() requires a secure context and isn't implemented by every
// browser/test environment this app runs in, hence the RFC 4122 v4 fallback.
const randomUuid = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

export type PurchasePhase = "idle" | "confirming" | "retrying" | "settled";

export type PurchaseOutcome =
  | { kind: "success"; response: FeatureCreditBuyResponse }
  | { kind: "declined"; description?: string }
  | { kind: "no_saved_card"; purchaseId?: number }
  | {
      kind: "reconciliation";
      variant: "still_confirming" | "captured_not_credited";
      purchaseId?: number;
      paymentId?: number | null;
      transactionUid?: string | null;
    }
  | { kind: "throttled" }
  | { kind: "stale" }
  | { kind: "error"; message: string };

interface BuyInput {
  packageId: number;
  currencyId: number;
  targetUserId?: number;
}

// Owns the parts of the credit-pack purchase flow that are genuine state-machine logic — the
// idempotency-key lifecycle, the ~16-outcome response table, and the bounded same-key retry — so
// BuyCreditsTab.tsx stays a thin renderer. See "The idempotency key contract" and "Handling the
// response" in feture-purchase-payplus-flow.md for the rules this implements.
//
// `enabled` mirrors the pre-existing `skip: !isAuth || !admin.id` guard the entitlements query was
// already called with, extended to the new eligibility query too — both are per-user reads that
// shouldn't fire before auth resolves.
export const useFeatureCreditPurchase = (enabled: boolean) => {
  // refetchOnMountOrArgChange because the answer turns on company state this screen never sees
  // change — a billing email added in company settings, by another admin, in another tab. The
  // endpoint provides no cache tag, so without this the cached canBuy:false outlives the fix and
  // the buyer is told to add an email they just added. It costs no provider round trip, which is
  // why the flow doc says to fire it on every mount.
  const eligibility = useGetPaymentMethodQuery(undefined, {
    skip: !enabled,
    refetchOnMountOrArgChange: true,
  });
  const [buyFeatureCredits] = useBuyFeatureCreditsMutation();
  const { refetch: refetchEntitlements } = useGetUserEntitlementsQuery(undefined, { skip: !enabled });

  const [phase, setPhase] = useState<PurchasePhase>("idle");
  const [outcome, setOutcome] = useState<PurchaseOutcome | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => randomUuid());

  const retryCountRef = useRef(0);
  const conflictRetriedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Starting a new attempt: package/scope/target changed, or the buyer explicitly starts over.
  // Never derived from packageId/targetUserId — a fresh random key each time, per the contract.
  const resetAttempt = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setPhase("idle");
    setOutcome(null);
    setIdempotencyKey(randomUuid());
  }, []);

  const attempt = useCallback(
    async (input: BuyInput, key: string) => {
      setPhase((prev) => (prev === "idle" ? "confirming" : "retrying"));

      try {
        const response = await buyFeatureCredits({
          packageId: input.packageId,
          currencyId: input.currencyId,
          scope: "company",
          targetUserId: input.targetUserId,
          idempotencyKey: key,
        }).unwrap();

        setIdempotencyKey(randomUuid());
        setPhase("settled");
        setOutcome({ kind: "success", response });
        refetchEntitlements();
        return;
      } catch (error) {
        const action = classifyBuyOutcome(error);
        const data = getBuyErrorData(error);

        switch (action) {
          case "retry-same-key": {
            if (retryCountRef.current < MAX_RETRIES) {
              retryCountRef.current += 1;
              setPhase("retrying");
              timeoutRef.current = setTimeout(() => attempt(input, key), RETRY_DELAY_MS);
            } else {
              setPhase("settled");
              setOutcome({
                kind: "reconciliation",
                variant: "still_confirming",
                purchaseId: data.purchaseId,
              });
            }
            return;
          }

          case "declined":
            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({ kind: "declined", description: data.description });
            return;

          case "conflict": {
            // The key is bound to a different buyer/package — usually a client that failed to
            // regenerate after a package change. One automatic resubmit with a fresh key; if it
            // recurs, that's a real client bug, not something to loop on.
            if (!conflictRetriedRef.current) {
              conflictRetriedRef.current = true;
              const newKey = randomUuid();
              setIdempotencyKey(newKey);
              await attempt(input, newKey);
              return;
            }

            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({ kind: "error", message: mapBuyError(error) });
            return;
          }

          case "no-saved-card":
            // Nothing was charged, but the purchase row is bound to this key. Reusing it would
            // replay that row instead of trying the card the buyer just added.
            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({ kind: "no_saved_card", purchaseId: data.purchaseId });
            return;

          case "captured-not-credited":
            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({
              kind: "reconciliation",
              variant: "captured_not_credited",
              purchaseId: data.purchaseId,
              paymentId: data.paymentId,
              transactionUid: data.transactionUid,
            });
            return;

          case "throttled":
            setPhase("settled");
            setOutcome({ kind: "throttled" });
            return;

          case "stale":
            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({ kind: "stale" });
            eligibility.refetch();
            return;

          case "terminal":
          default:
            // Nothing was created (or, for a settle-path 400 like company_missing_billing_email,
            // the row is bound to the current key already) — either way a next attempt needs a new
            // key, since reusing this one would 409 rather than start fresh.
            setIdempotencyKey(randomUuid());
            setPhase("settled");
            setOutcome({ kind: "error", message: mapBuyError(error) });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buyFeatureCredits, refetchEntitlements]
  );

  const submit = useCallback(
    (input: BuyInput) => {
      retryCountRef.current = 0;
      conflictRetriedRef.current = false;
      setOutcome(null);
      void attempt(input, idempotencyKey);
    },
    [attempt, idempotencyKey]
  );

  return {
    eligibility,
    phase,
    outcome,
    // Only while a request is in flight. "settled" means the answer is in, so the picker and the
    // Confirm button are usable again without dismissing the outcome banner first.
    isBusy: phase === "confirming" || phase === "retrying",
    submit,
    resetAttempt,
  };
};
